import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OktaAuthService } from './okta-auth.service';
import { 
  Observable, 
  switchMap, 
  tap, 
  throwError, 
  map, 
  BehaviorSubject, 
  catchError, 
  take, 
  EMPTY,
  of
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {  MessageResponse, SalesforceToken } from '@eda/shared/models/message.model';
import { ConversationIdGenerator } from '@eda/shared/conversation-id-generator';
import { CacheFactoryService } from '@eda/shared/cache/cache-factory';
import { ConversationType } from '@eda/shared/enums/conversation-type';

export interface FeedbackSession {
  sessionId: string;
  isActive: boolean;
}

/**
 * Payload sent to the feedback endpoint.
 * text is optional and can be submitted later as follow-up feedback.
 */
export interface FeedbackRequest {
  feedbackId: string;
  feedback: string;
  text?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EdaApiService {
  
  private httpClient = inject(HttpClient);
  private oktaAuthService = inject(OktaAuthService);
  private conversationIdGenerator = inject(ConversationIdGenerator);
  private cachecFactory = inject(CacheFactoryService);

  private sessionSubject = new BehaviorSubject<FeedbackSession | null>(null);
  private session$ = this.sessionSubject.asObservable();
  public feedbackIdSubject = new BehaviorSubject<string | null>(null);
  public feedbackId$ = this.feedbackIdSubject.asObservable();

  private readonly SALESFORCE_TOKEN_CACHE_KEY = 'salesforce_token_cache_key';

  /**
   * Executes an API call only when an Okta access token is available.
   * Optionally redirects the user to login if token retrieval fails.
   */
  private withAccessToken<T>(requestFactory: (accessToken: string) => Observable<T>, redirectToLogin = false): Observable<T> {
    return this.oktaAuthService.getAccessToken().pipe(
      switchMap((token) => {
        const accessToken = token?.accessToken;
        if (!accessToken) {
          if (redirectToLogin) {
            this.oktaAuthService.login(window.location.href);
          }
          return throwError(() => new Error('No access token available'));
        }
        return requestFactory(accessToken);
      })
    );
  }

  private buildAuthHeaders(accessToken: string, additionalHeaders: Record<string, string> = {}): Record<string, string> {
    return {
      'Authorization': `Bearer ${accessToken}`,
      'api-key': environment.apigeeConfig.clientId,
      ...additionalHeaders
    };
  }

  private endFeedbackSessionRequest(sessionId: string, accessToken: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.apigeeConfig.domain}${environment.apigeeConfig.endFeedbackSessionEndpoint.replace('{sessionId}', sessionId)}`,
      {
        headers: this.buildAuthHeaders(accessToken, {
          'x-session-end-reason': 'UserRequest'
        })
      }
    );
  }

  /**
   * Closes the server-side feedback session and marks local state inactive.
   */
  private closeFeedbackSession(session: FeedbackSession, accessToken: string): Observable<void> {
    return this.endFeedbackSessionRequest(session.sessionId, accessToken).pipe(
      tap(() => this.sessionSubject.next({ ...session, isActive: false }))
    );
  }

  /**
   * Sends a chat message to the backend using a generated conversation id.
   */
  sendMessage(messageToSend: string): Observable<MessageResponse> {
    return this.withAccessToken((accessToken) => {
        const convoId = this.conversationIdGenerator.generateConversationId();
        const messageEndpoint = environment.apigeeConfig.messsageEndpoint.replace('{convId}', convoId);
        const endpointToSendMessage = `${environment.apigeeConfig.domain}${messageEndpoint}`;
        const messageId = crypto.randomUUID();
        return this.httpClient.post<MessageResponse>(endpointToSendMessage, {
          message: {
            id: messageId,
            staticContent: {
              formatType: 'Text',
              text: messageToSend.trim()
            }
         } }, {
          headers: {
            ...this.buildAuthHeaders(accessToken, {
              'Content-Type': 'application/json'
            })
          }
        })
      }, true);
  }

  generateConversationSession(): Observable<any> {
    return this.withAccessToken((accessToken) => {
        const convoId = this.conversationIdGenerator.generateConversationId();
        const generateConvesationEndpoint = `${environment.apigeeConfig.domain}${environment.apigeeConfig.conversationGenerationEndpoint}`;
        return this.httpClient.post<any>(generateConvesationEndpoint, {conversationId:convoId,sourcePortal:environment.salesForceConfig.sourcePortal}, {
          headers: {
            ...this.buildAuthHeaders(accessToken, {
              'Content-Type': 'application/json'
            })
          }
        });
      }
    );
  }

  generateSalesforceToken(): Observable<SalesforceToken> {
    if (this.cachecFactory.isCacheAvailable(this.SALESFORCE_TOKEN_CACHE_KEY)) { 
      return this.cachecFactory.getCache(this.SALESFORCE_TOKEN_CACHE_KEY) as Observable<SalesforceToken>;
    }
    return this.withAccessToken((accessToken) => {
        const generateConvesationEndpoint = `${environment.apigeeConfig.domain}${environment.apigeeConfig.salesForceTokenEndpoint}`;
        return this.httpClient.get<SalesforceToken>(generateConvesationEndpoint, {
          headers: {
            ...this.buildAuthHeaders(accessToken, {
              'Content-Type': 'application/json',
              'X-Org-Id': environment.salesForceConfig.orgId
            })
          }
        }).pipe(
          tap((salesforceToken: SalesforceToken) => {
            this.cachecFactory.setCacheable({
              key: this.SALESFORCE_TOKEN_CACHE_KEY,
              toCache: salesforceToken
            });
          })
        );
      }
    );
  }

  typingIndicator(entryType: ConversationType, id: string) {
     return this.withAccessToken((accessToken) => {
        const convoId = this.conversationIdGenerator.generateConversationId();
        const typingIndicatorEndpoint = `${environment.apigeeConfig.domain}${environment.apigeeConfig.typingIndicatorEndpoint.replace('{convId}',convoId)}`;
        return this.httpClient.post<any>(typingIndicatorEndpoint, {entryType,id}, {
          headers: {
            ...this.buildAuthHeaders(accessToken, {
              'Content-Type': 'application/json'
            })
          }
        });
      }
    );
  }

  closeConversation() {
    return this.withAccessToken((accessToken) => {
        const convoId = this.conversationIdGenerator.popConversationId();
        const closeConversationEndpoint = `${environment.apigeeConfig.domain}${environment.apigeeConfig.closeConversationEndpoint.replace('{convId}', convoId)}`;
        return this.httpClient.post<any>(closeConversationEndpoint, {}, {
          headers: {
            ...this.buildAuthHeaders(accessToken, {
              'Content-Type': 'application/json'
            })
          }
        });
      }
    );
  }

  /**
   * Creates and stores an active feedback session id.
    * The generated id is returned to the caller for component-level tracking.
   */
  createFeedbackSession(): Observable<string> {
    const sessionId: string = crypto.randomUUID();
    return this.withAccessToken((accessToken) => {
        return this.httpClient.post<{ sessionId: string }>(
          `${environment.apigeeConfig.domain}${environment.apigeeConfig.startFeedbackSessionEndpoint}`,
          { 'externalSessionKey': sessionId },
          {
            headers: {
              ...this.buildAuthHeaders(accessToken)
            },
            observe: 'response'
          }
        );
      }).pipe(
      map(response => {
        const feedbackSessionId = response.body?.sessionId ?? sessionId;

        const session: FeedbackSession = { sessionId: feedbackSessionId, isActive: true };
        this.sessionSubject.next(session);
        return feedbackSessionId;
      }),
      catchError(error => {
        console.error('Error creating feedback session:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Submits thumbs up/down feedback for the current active session.
   * If text is not empty string, the session is closed after submission.
    * If text is empty string, the session remains open for optional follow-up text.
   */
  submitFeedback(isLiked: boolean, text?: string): Observable<void> {
    const session = this.sessionSubject.getValue();
    if (!session?.sessionId || !session.isActive) {
      return throwError(() => new Error('No active sessions'));
    }

    const feedbackReq: FeedbackRequest = {
      feedbackId: this.feedbackIdSubject.getValue() ?? session.sessionId,
      feedback: isLiked ? 'GOOD' : 'BAD',
      text
    };

    return this.withAccessToken((accessToken) => {
      const authHeaders = this.buildAuthHeaders(accessToken);
      const submitFeedbackEndpoint = `${environment.apigeeConfig.domain}${environment.apigeeConfig.submitFeedbackEndpoint.replace('{sessionId}', session.sessionId)}`;

      return this.httpClient.post<void>(submitFeedbackEndpoint, feedbackReq, { headers: authHeaders }).pipe(
        switchMap(() => text !== '' ? this.closeFeedbackSession(session, accessToken) : of(void 0)),
        catchError(error => {
          console.error('Error submitting feedback:', error);
          return throwError(() => error);
        })
      );
    });
  }

  /**
   * Explicitly ends the current active feedback session if one exists.
   */
  endSession(): Observable<void> {
    return this.session$.pipe(
      take(1),
      switchMap(session => {
        if (!session?.sessionId || !session.isActive) {
          return EMPTY;
        }
        return this.withAccessToken((accessToken) => {
          return this.closeFeedbackSession(session, accessToken);
        });
      })
    );
  }

  /**
   * Clears local feedback session state without making an API request.
   */
  clearSession(): void {
    this.sessionSubject.next(null);
    this.feedbackIdSubject.next(null);
  }

}
